import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, Upload, Trash2, Edit, Eye, Download, Search,
  Globe, Calendar, User, FileType, AlertCircle, Plus
} from 'lucide-react';
import axios from 'axios';
import { toast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';

interface Site {
  id: string;
  url: string;
  name: string;
}

interface Document {
  id: string;
  site_id: string;
  title: string;
  content: string;
  document_type: 'manual' | 'research' | 'faq' | 'policy' | 'guide' | 'other';
  metadata?: {
    source?: string;
    author?: string;
    tags?: string[];
    topic_id?: string;
    topic_name?: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

const DOCUMENT_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'research', label: 'Research' },
  { value: 'faq', label: 'FAQ' },
  { value: 'policy', label: 'Policy' },
  { value: 'guide', label: 'Guide' },
  { value: 'other', label: 'Other' }
];

export default function MyKnowledge() {
  const { user, token } = useAuth();
  const { selectedSite } = useSite();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [uploadDialog, setUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [newDocument, setNewDocument] = useState({
    title: '',
    content: '',
    document_type: 'manual',
    metadata: {
      tags: [] as string[],
      source: '',
      author: user?.name || user?.email || ''
    }
  });

  // Fetch user's sites
  const { data: sites } = useQuery<Site[]>({
    queryKey: ['my-sites'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/sites/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ensure we always return an array
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!token
  });

  // Fetch site documents
  const { data: documents, refetch: refetchDocuments } = useQuery<Document[]>({
    queryKey: ['site-documents', selectedSite?.id || selectedSite?._id],
    queryFn: async () => {
      const siteId = selectedSite?._id || selectedSite?.id;
      const response = await axios.get(`${API_URL}/site-documents/site/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API returns { success: true, documents: [...] }
      // Extract the documents array from the response
      return response.data?.documents || [];
    },
    enabled: !!selectedSite && !!token
  });

  const handleUploadDocument = async () => {
    try {
      const siteId = selectedSite?._id || selectedSite?.id;
      await axios.post(
        `${API_URL}/site-documents`,
        {
          site_id: siteId,
          ...newDocument,
          metadata: {
            ...newDocument.metadata,
            tags: newDocument.metadata.tags.filter(t => t.length > 0)
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Document uploaded",
        description: "Your document has been added to the knowledge base"
      });

      setUploadDialog(false);
      setNewDocument({
        title: '',
        content: '',
        document_type: 'manual',
        metadata: {
          tags: [],
          source: '',
          author: user?.name || user?.email || ''
        }
      });
      refetchDocuments();
    } catch (error) {
      toast({
        title: "Failed to upload document",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleUpdateDocument = async () => {
    if (!selectedDocument) return;

    try {
      await axios.put(
        `${API_URL}/site-documents/${selectedDocument.id}`,
        selectedDocument,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Document updated",
        description: "Your changes have been saved"
      });

      setEditDialog(false);
      setSelectedDocument(null);
      refetchDocuments();
    } catch (error) {
      toast({
        title: "Failed to update document",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(
        `${API_URL}/site-documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Document deleted",
        description: "The document has been removed from your knowledge base"
      });

      refetchDocuments();
    } catch (error) {
      toast({
        title: "Failed to delete document",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (document: Document) => {
    try {
      await axios.put(
        `${API_URL}/site-documents/${document.id}`,
        { ...document, is_active: !document.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: document.is_active ? "Document deactivated" : "Document activated",
        description: document.is_active 
          ? "This document will no longer be used by the AI"
          : "This document is now available to the AI"
      });

      refetchDocuments();
    } catch (error) {
      toast({
        title: "Failed to update document status",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const filteredDocuments = Array.isArray(documents) ? documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    return matchesSearch && matchesType;
  }) : [];

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case 'research': return '🔬';
      case 'faq': return '❓';
      case 'policy': return '📋';
      case 'guide': return '📖';
      case 'manual': return '📚';
      default: return '📄';
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Knowledge</h1>
          <p className="text-muted-foreground">Manage documents and content for your sites</p>
        </div>
      </div>

      {/* Site Information - handled by global context */}
      {!selectedSite ? (
        <Card className="mb-6">
          <CardContent className="py-8">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Please select a site from the header to manage its knowledge base</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Current Site</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-2">
              <Globe className="w-4 h-4" />
              {selectedSite?.name || selectedSite?.url}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {selectedSite && (
        <>
          {/* Search and Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {DOCUMENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                  <DialogDescription>
                    Add a new document to your site's knowledge base
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="doc-title">Title</Label>
                    <Input
                      id="doc-title"
                      value={newDocument.title}
                      onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                      placeholder="Document title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="doc-type">Document Type</Label>
                    <Select
                      value={newDocument.document_type}
                      onValueChange={(value) => setNewDocument({ ...newDocument, document_type: value as any })}
                    >
                      <SelectTrigger id="doc-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="doc-content">Content</Label>
                    <Textarea
                      id="doc-content"
                      value={newDocument.content}
                      onChange={(e) => setNewDocument({ ...newDocument, content: e.target.value })}
                      placeholder="Document content..."
                      rows={10}
                    />
                  </div>
                  <div>
                    <Label htmlFor="doc-tags">Tags (comma-separated)</Label>
                    <Input
                      id="doc-tags"
                      value={newDocument.metadata.tags.join(', ')}
                      onChange={(e) => setNewDocument({
                        ...newDocument,
                        metadata: {
                          ...newDocument.metadata,
                          tags: e.target.value.split(',').map(t => t.trim())
                        }
                      })}
                      placeholder="tag1, tag2, tag3"
                    />
                  </div>
                  <div>
                    <Label htmlFor="doc-source">Source (optional)</Label>
                    <Input
                      id="doc-source"
                      value={newDocument.metadata.source}
                      onChange={(e) => setNewDocument({
                        ...newDocument,
                        metadata: { ...newDocument.metadata, source: e.target.value }
                      })}
                      placeholder="Source URL or reference"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUploadDocument}>
                    Upload Document
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Documents Grid */}
          {filteredDocuments.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchTerm || filterType !== 'all' ? 'No documents found' : 'No documents yet'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || filterType !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Start building your knowledge base by uploading documents'
                  }
                </p>
                {!searchTerm && filterType === 'all' && (
                  <Button onClick={() => setUploadDialog(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Your First Document
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredDocuments.map((document) => (
                <Card key={document.id} className={!document.is_active ? 'opacity-60' : ''}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <span>{getDocumentTypeIcon(document.document_type)}</span>
                          {document.title}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          <Badge variant="outline" className="mb-2">
                            {DOCUMENT_TYPES.find(t => t.value === document.document_type)?.label}
                          </Badge>
                        </CardDescription>
                      </div>
                      <Badge variant={document.is_active ? "success" : "secondary"}>
                        {document.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {document.content}
                    </p>
                    
                    {document.metadata?.tags && document.metadata.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {document.metadata.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(document.created_at).toLocaleDateString()}
                      </span>
                      {document.created_by && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {document.created_by}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDocument(document);
                          setViewDialog(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDocument(document);
                          setEditDialog(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(document)}
                      >
                        {document.is_active ? '🚫' : '✅'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(document.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* View Dialog */}
          <Dialog open={viewDialog} onOpenChange={setViewDialog}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedDocument?.title}</DialogTitle>
                <DialogDescription>
                  {DOCUMENT_TYPES.find(t => t.value === selectedDocument?.document_type)?.label} •
                  Created {selectedDocument && new Date(selectedDocument.created_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="whitespace-pre-wrap">{selectedDocument?.content}</div>
                {selectedDocument?.metadata?.tags && selectedDocument.metadata.tags.length > 0 && (
                  <div>
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedDocument.metadata.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedDocument?.metadata?.source && (
                  <div>
                    <Label>Source</Label>
                    <p className="text-sm text-muted-foreground">{selectedDocument.metadata.source}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={editDialog} onOpenChange={setEditDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Document</DialogTitle>
                <DialogDescription>
                  Make changes to your document
                </DialogDescription>
              </DialogHeader>
              {selectedDocument && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-title">Title</Label>
                    <Input
                      id="edit-title"
                      value={selectedDocument.title}
                      onChange={(e) => setSelectedDocument({ ...selectedDocument, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-type">Document Type</Label>
                    <Select
                      value={selectedDocument.document_type}
                      onValueChange={(value) => setSelectedDocument({ ...selectedDocument, document_type: value as any })}
                    >
                      <SelectTrigger id="edit-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit-content">Content</Label>
                    <Textarea
                      id="edit-content"
                      value={selectedDocument.content}
                      onChange={(e) => setSelectedDocument({ ...selectedDocument, content: e.target.value })}
                      rows={10}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-tags">Tags (comma-separated)</Label>
                    <Input
                      id="edit-tags"
                      value={selectedDocument.metadata?.tags?.join(', ') || ''}
                      onChange={(e) => setSelectedDocument({
                        ...selectedDocument,
                        metadata: {
                          ...selectedDocument.metadata,
                          tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                        }
                      })}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setEditDialog(false);
                  setSelectedDocument(null);
                }}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateDocument}>
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}